import React, {useEffect, useRef, useState} from 'react';
import {Project} from '../domain/types';
import {ChevronDownIcon, FolderIcon, PlusIcon, StarIcon} from './Icons';

interface ProjectSwitcherProps {
  projects: Project[];
  currentProject: Project | null;
  onSwitch: (projectId: string) => void;
  onViewAll: () => void;
}

/** Favourites first, then the five most recently touched. */
function partition(projects: Project[]) {
  const favorites = projects.filter(p => p.favorite);
  const recent = projects
    .filter(p => !p.favorite)
    .sort((a, b) => (
      new Date(b.lastActivity || b.updatedAt).getTime()
      - new Date(a.lastActivity || a.updatedAt).getTime()
    ))
    .slice(0, 5);

  return {favorites, recent};
}

/**
 * The project menu, once.
 *
 * The header used to carry two copies of this list — one for "a project is selected" and one for
 * "none is" — which is how they drifted apart. The only real difference is the label on the button,
 * so that is the only thing branched on here.
 */
const ProjectSwitcher: React.FC<ProjectSwitcherProps> = ({
  projects,
  currentProject,
  onSwitch,
  onViewAll,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const {favorites, recent} = partition(projects);

  const choose = (projectId: string) => {
    onSwitch(projectId);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        title="Switch project"
        className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm text-gray-300
         hover:bg-gray-800 hover:text-white transition-colors cursor-pointer"
      >
        <FolderIcon className="w-4 h-4 text-gray-500" />
        {/* Narrower on phones so a long project name can't be the thing that pushes the header's
            right-hand cluster (quota, upload, profile) off the edge of the screen. */}
        <span className="max-w-[5rem] truncate sm:max-w-[10rem]">
          {currentProject ? currentProject.name : 'Select a project'}
        </span>
        <ChevronDownIcon className="w-3.5 h-3.5 text-gray-600" />
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute left-0 top-full z-50 mt-1 max-h-96 w-64 overflow-y-auto rounded-xl
           border border-gray-800 bg-gray-900 shadow-2xl shadow-black/50"
        >
          {projects.length === 0 && (
            <p className="px-3 py-4 text-center text-xs text-gray-500">No projects yet.</p>
          )}

          <ProjectGroup
            heading="Favourites"
            icon={<StarIcon className="w-3.5 h-3.5 shrink-0 text-amber-400" />}
            projects={favorites}
            currentId={currentProject?.id}
            onChoose={choose}
          />
          <ProjectGroup
            heading="Recent"
            icon={<FolderIcon className="w-3.5 h-3.5 shrink-0 text-gray-600" />}
            projects={recent}
            currentId={currentProject?.id}
            onChoose={choose}
          />

          <button
            type="button"
            onClick={() => {
              onViewAll();
              setIsOpen(false);
            }}
            className="flex w-full items-center gap-1.5 border-t border-gray-800 px-3 py-2 text-left
             text-xs text-indigo-400 hover:bg-gray-800 transition-colors cursor-pointer"
          >
            <PlusIcon className="w-3.5 h-3.5" />
            View all projects
          </button>
        </div>
      )}
    </div>
  );
};

const ProjectGroup: React.FC<{
  heading: string;
  icon: React.ReactNode;
  projects: Project[];
  currentId?: string;
  onChoose: (projectId: string) => void;
}> = ({heading, icon, projects, currentId, onChoose}) => {
  if (projects.length === 0) {
    return null;
  }

  return (
    <>
      <p className="px-3 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-gray-600">
        {heading}
      </p>
      {projects.map(project => (
        <button
          key={project.id}
          type="button"
          role="menuitem"
          onClick={() => onChoose(project.id)}
          className={`flex w-full items-center gap-2 px-3 py-2 text-left transition-colors cursor-pointer ${
            project.id === currentId ? 'bg-gray-800 text-white' : 'text-gray-300 hover:bg-gray-800/60'
          }`}
        >
          {icon}
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm">{project.name}</span>
            {project.description && (
              <span className="block truncate text-xs text-gray-500">{project.description}</span>
            )}
          </span>
        </button>
      ))}
    </>
  );
};

export default ProjectSwitcher;
